import { Box, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import React, { useCallback, useState } from 'react';

import ArrowDownIcon from '../../../../packages/common/shared-ui/src/icons/ArrowDownIcon';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderRadius: '16px',
      backgroundColor: '#fff',
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));
const ProjectsListView = ({ projects, projectscColumns, changeSorting }) => {
  const getLocalString = useTranslation();
  const [sortModel, setSortModel] = useState([]);
  const handleGetRedirectUrl = useCallback(({ id, columnType }) => {
    switch (columnType) {
      case 'project':
        return `${window.location.origin}/projects/${id}`;
      default:
        return '';
    }
  }, []);
  const cellRenderer = useCallback(
    ({ props, column }) => {
      const linkFields = ['project'];
      if (linkFields.includes(column.fieldType)) {
        const link = handleGetRedirectUrl({
          id: props.id,
          columnType: column.fieldType,
        });
        return (
          <Box
            sx={{
              wordBreak: 'break-word',
              display: 'flex',
              alignItems: 'center',
              padding:
                props.row.level > 1 ? `0 0 0 ${props.row.level * 8}px` : '0',
            }}
          >
            {props.row.isParent && <ArrowDownIcon></ArrowDownIcon>}
            <Link
              href={link}
              sx={{
                color: '#212346',
              }}
            >
              {props.value}{' '}
              <Box
                component={'span'}
                sx={{ color: props.row.status == 1 ? '#37C670' : '#E03737' }}
              >
                •
              </Box>
            </Link>
          </Box>
        );
      }
    },

    [handleGetRedirectUrl],
  );
  const formattedProjectsColumns = useCallback(() => {
    const projectsColumns = projectscColumns;
    const smallFields = ['date', 'hours', 'float', 'bool'];
    return projectsColumns.map(column => {
      return {
        field: column.fieldId,
        headerName: column.fieldName,
        renderHeader: props => (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {props.colDef.headerName}
          </Typography>
        ),
        disableExport: false,
        sortable: column.canSort,
        position: column.position,
        flex: smallFields.includes(column.fieldType) ? 0 : 1,
        width: smallFields.includes(column.fieldType) && 120,
        renderCell: props => cellRenderer({ props, column }),
        cellClassName: props =>
          props.colDef.field === 'project.name' && props.row.isParent
            ? 'tableCell parent'
            : 'tableCell',
      };
    });
  }, [cellRenderer, projectscColumns]);
  const formattedProjectsRows = useCallback(
    () =>
      projects.reduce((acc, project) => {
        if (project.rowType === 'total') {
          const newCols = project.columns.reduce((columnAcc, _, i) => {
            return {
              ...columnAcc,
              [project.columns[i].fieldId]: project.columns[i].value,
            };
          }, {});
          return [
            ...acc,
            {
              id: project.rowId,
              ...newCols,
              status: project.services.status,
              level: project.services.level,
              isParent: project.services.isParent,
            },
          ];
        }
        return [];
      }, []),
    [projects],
  );
  const projectRows = formattedProjectsRows();
  const projectsColumns = formattedProjectsColumns();
  const handleSorting = useCallback(
    newSortModel => {
      const validSortModel = newSortModel.filter(
        item =>
          item &&
          typeof item.field === 'string' &&
          ['asc', 'desc'].includes(item.sort),
      );

      setSortModel(validSortModel);
      changeSorting(validSortModel);
    },
    [changeSorting],
  );
  return (
    <>
      <StyledDataGridTableRoot
        localeText={{
          noRowsLabel: getLocalString('labels', 'noData'),
        }}
        hideFooter
        disableColumnFilter
        disableRowSelectionOnClick
        disableColumnMenu
        columns={projectsColumns}
        rows={projectRows}
        getRowId={item => item.id}
        sortModel={Array.isArray(sortModel) ? sortModel : []}
        onSortModelChange={handleSorting}
        sortingMode='server'
        slots={{
          columnUnsortedIcon: () => (
            <SortIcon width='20' height='20' viewBox='0 0 20 20' />
          ),
          columnSortedAscendingIcon: () => (
            <SortIcon
              direction='up'
              width='20'
              height='20'
              viewBox='0 0 20 20'
            />
          ),
          columnSortedDescendingIcon: () => (
            <SortIcon
              direction='down'
              width='20'
              height='20'
              viewBox='0 0 20 20'
            />
          ),
        }}
        getRowClassName={params => params.formattedValue && 'groupRow'}
        getRowHeight={() => 'auto'}
      />
    </>
  );
};
export default ProjectsListView;
